import React, { useCallback } from 'react';
import { Radio, Typography } from '@mui/material';
import styled from 'styled-components';

import { Action } from 'state';
import { colors } from 'theme';
import Choice from 'types/Choice';
import Section from 'types/Section';
import Field, { getNestedFieldsByChoice } from 'types/Field';
import NestedSelectFieldPreview from './NestedSelectFieldPreview';

const StyledChoiceRow = styled.div`
  display: flex;
  justify-content: space-between;
  height: 2rem;

  padding: 0.4rem 0.8rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;

  background-color: ${colors.blueGrey_10};
`;

const StyledRadio = styled(Radio)`
  &.MuiIconButton-root {
    padding: 0;
  }

  &.MuiRadio-colorSecondary.Mui-checked {
    color: ${colors.primary};
  }
`;

interface SingleSelectFieldPreviewProps {
  field: Field;
  section: Section;
  dispatch: (action: Action) => void;
}

const SingleSelectFieldPreview: React.FC<SingleSelectFieldPreviewProps> = ({ field, section, dispatch }) => {
  const nestedFieldsByChoice = getNestedFieldsByChoice(field, section.exam_fields);

  const handleOnChange = useCallback(
    (choice: Choice) => {
      dispatch({ type: 'toggle-single-choice-preview', section, field, choice });
    },
    [dispatch, section, field],
  );

  const renderNestedFieldRow = (choice: Choice): React.ReactNode => {
    const nestedField = nestedFieldsByChoice[choice.uuid];

    if (!nestedField) return null;
    if (!field.preview_default_value.includes(choice.uuid)) return null;

    return <NestedSelectFieldPreview section={section} field={nestedField} dispatch={dispatch} />;
  };

  return (
    <>
      <Typography variant="subtitle2">{field.label}</Typography>
      {field.choices_json.map((choice) => (
        <div key={choice.uuid}>
          <StyledChoiceRow>
            <Typography variant="body2">{choice.label}</Typography>
            <StyledRadio
              checked={field.preview_default_value.includes(choice.uuid)}
              value={choice.uuid}
              onChange={() => handleOnChange(choice)}
            />
          </StyledChoiceRow>
          {renderNestedFieldRow(choice)}
        </div>
      ))}
    </>
  );
};

export default SingleSelectFieldPreview;
